import { Box, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CpdTextField from '@/components/ui/CpdTextField';
import ConfigurationField from './ConfigurationField';
import SectionHeader from './SectionHeader';
import {
  SECTION_BOTTOM_MARGIN,
  SECTION_GRID_SPACING,
  SFTP_AUTHENTICATION_MODE,
} from '../paymentsManagerConfiguration.constants';

const SftpAuthenticationSection = ({
  form,
  fieldErrors,
  passwordConfigured,
  privateKeyConfigured,
  onFieldChange,
  onModeChange,
  disabled,
}) => {
  const { t } = useTranslation();

  const getPasswordTooltip = () => {
    if (passwordConfigured) {
      return t('managedAccounts.paymentsManager.authentication.passwordSaved');
    }
    return t('managedAccounts.paymentsManager.authentication.passwordRequired');
  };

  const getPrivateKeyHelperText = () => {
    if (fieldErrors.private_key) {
      return fieldErrors.private_key;
    }
    if (privateKeyConfigured) {
      return t(
        'managedAccounts.paymentsManager.authentication.privateKeySaved',
      );
    }
    return t(
      'managedAccounts.paymentsManager.authentication.privateKeyRequired',
    );
  };

  const renderCredentialField = () => {
    if (form.authentication_mode === SFTP_AUTHENTICATION_MODE.PASSWORD) {
      return (
        <Grid item xs={12} md={6}>
          <ConfigurationField
            name='password'
            label={t('managedAccounts.paymentsManager.authentication.password')}
            value={form.password}
            onChange={onFieldChange}
            error={fieldErrors.password}
            type='password'
            disabled={disabled}
            required={!passwordConfigured}
            tooltip={getPasswordTooltip()}
          />
        </Grid>
      );
    }

    return (
      <>
        <Grid item xs={12}>
          <CpdTextField
            id='private_key'
            name='private_key'
            label={t(
              'managedAccounts.paymentsManager.authentication.privateKey',
            )}
            value={form.private_key}
            onChange={onFieldChange}
            error={Boolean(fieldErrors.private_key)}
            helperText={getPrivateKeyHelperText()}
            disabled={disabled}
            required={!privateKeyConfigured}
            multiline
            minRows={5}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 0 }}
          />
        </Grid>
        <Grid item xs={12}>
          <CpdTextField
            id='public_key'
            name='public_key'
            label={t(
              'managedAccounts.paymentsManager.authentication.publicKey',
            )}
            value={form.public_key}
            onChange={onFieldChange}
            error={Boolean(fieldErrors.public_key)}
            helperText={fieldErrors.public_key}
            disabled={disabled}
            multiline
            minRows={3}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 0 }}
          />
        </Grid>
      </>
    );
  };

  const handleAuthenticationModeChange = (_event, useSshKey) => {
    if (useSshKey) {
      onModeChange(SFTP_AUTHENTICATION_MODE.SSH_KEY);
      return;
    }

    onModeChange(SFTP_AUTHENTICATION_MODE.PASSWORD);
  };

  return (
    <>
      <SectionHeader>
        {t('managedAccounts.paymentsManager.authentication.title')}
      </SectionHeader>
      <Grid
        container
        spacing={SECTION_GRID_SPACING}
        sx={{ mb: SECTION_GRID_SPACING }}>
        <Grid item xs={12} md={6}>
          <ConfigurationField
            name='username'
            label='Username'
            value={form.username}
            onChange={onFieldChange}
            error={fieldErrors.username}
            disabled={disabled}
            tooltip='Username used to authenticate with the SFTP server'
          />
        </Grid>
      </Grid>
      <Box sx={{ mb: SECTION_GRID_SPACING }}>
        <FormControlLabel
          control={
            <Switch
              name='authentication_mode'
              checked={
                form.authentication_mode === SFTP_AUTHENTICATION_MODE.SSH_KEY
              }
              onChange={handleAuthenticationModeChange}
              disabled={disabled}
            />
          }
          label={t('managedAccounts.paymentsManager.authentication.mode')}
        />
        {fieldErrors.authentication_mode && (
          <Typography variant='caption' color='error' sx={{ display: 'block' }}>
            {fieldErrors.authentication_mode}
          </Typography>
        )}
      </Box>
      <Grid
        container
        spacing={SECTION_GRID_SPACING}
        sx={{ mb: SECTION_BOTTOM_MARGIN }}>
        {renderCredentialField()}
      </Grid>
    </>
  );
};

export default SftpAuthenticationSection;
